import { useRef, useState } from "react";
import { StreamContext } from "../../utilities/barcode-scanner/context";
import { useScanner } from "../../utilities/barcode-scanner/hooks";

export interface StreamProviderProps {
  children?: React.ReactNode;
}

export function StreamProvider({ children }: StreamProviderProps) {
  const streamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isCameraReady, setIsCameraReady] = useState<boolean>(false);

  const scannerController = useScanner({
    streamRef,
    videoRef,
    cleanupStream,
    setVideoSource,
    setIsCameraReady,
  });

  function stopMediaStream(stream: MediaStream | null) {
    if (!stream) {
      return;
    }
    if (stream.getAudioTracks) {
      stream.getAudioTracks().forEach((track) => track.stop());
    }
    if (stream.getVideoTracks) {
      stream.getVideoTracks().forEach((track) => track.stop());
    }
    if (isMediaStreamTrack(stream)) {
      stream.stop();
    }
  }

  function setVideoSource(
    video: HTMLVideoElement,
    stream: MediaStream | MediaSource | Blob | null
  ): void {
    const createObjectURL =
      window.URL.createObjectURL ?? window.webkitURL.createObjectURL;

    if (typeof video.srcObject !== "undefined") {
      video.srcObject = stream;
    } else if (typeof (video as any).mozSrcObject !== "undefined") {
      (video as any).mozSrcObject = stream;
    } else if (stream && createObjectURL) {
      video.src = createObjectURL(stream as MediaSource | Blob);
    }

    if (!stream) {
      const revokeObjectURL =
        window.URL.revokeObjectURL ?? window.webkitURL.revokeObjectURL;
      const source =
        video.src ?? video.srcObject ?? (video as any).mozSrcObject;
      if (revokeObjectURL && typeof source === "string") {
        revokeObjectURL(source);
      }
    }
  }

  function cleanupStream() {
    scannerController.destroy();
    setIsCameraReady(false);
    stopMediaStream(streamRef.current);
  }

  return (
    <StreamContext.Provider
      value={{
        streamRef,
        videoRef,
        isCameraReady,
        cleanupStream,
        setVideoSource,
        scannerController,
      }}
    >
      {children}
    </StreamContext.Provider>
  );
}

function isMediaStreamTrack(input: any): input is MediaStreamTrack {
  return typeof input.stop === "function";
}
